import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useLazyPingUserActivityQuery } from '../redux/apiSlice';

/**
 * Hook to ping user activity every 2 minutes when user is authenticated
 */
export const useUserActivityPing = () => {
  const isAuthenticated = useSelector(
    (state: RootState) => state.app.user.isAuthenticated,
  );
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Use lazy query hook - allows manual triggering
  const [triggerPing] = useLazyPingUserActivityQuery();

  useEffect(() => {
    if (isAuthenticated) {
      // Ping immediately when authenticated
      triggerPing().catch((error) => {
        // Silently handle errors - activity ping failures shouldn't break the app
        console.debug('User activity ping failed:', error);
      });

      // Set up interval to ping every 2 minutes (120000 ms)
      intervalRef.current = setInterval(() => {
        triggerPing().catch((error) => {
          // Silently handle errors - activity ping failures shouldn't break the app
          console.debug('User activity ping failed:', error);
        });
      }, 1000 * 60); // 1 minutes = 60000 milliseconds
    } else {
      // Clear interval when user is not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    // Cleanup on unmount or when authentication changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, triggerPing]);
};
